// src/pages/Cart.jsx

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaPlus,
  FaMinus,
  FaTrashAlt,
  FaShoppingCart,
  FaTruck,
} from "react-icons/fa";
import { useCart } from "../context/CartContext";
import { useNavigate, Link } from "react-router-dom";
import { getImageUrl } from "../utils/imageUrl";
import { ENABLE_CHECKOUT_UI } from "../config/featureFlags";
import {
  SHIPPING_CONFIG_DEFAULTS,
  fetchShippingConfig,
} from "../config/shippingConfig";

// --- Helper Component: Cart Item Row for the main page ---
const CartItemRow = ({ item, updateItemQuantity, removeItemFromCart, t }) => {
  // ✅ HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(item.quantity.toString());

  const product = item.Product;
  if (!product) return null;

  const itemPrice = parseFloat(product.price);
  const validItemPrice = isNaN(itemPrice) ? 0 : itemPrice;
  const lineTotal = validItemPrice * item.quantity;

  const productName = product[`name_${t("language")}`] || product.name_en;

  const imageUrl =
    product.images && product.images[0]
      ? getImageUrl(product.images[0].image_url)
      : `https://placehold.co/100x100/E0BBE4/FFFFFF?text=No+Image`;

  // ✅ CHECK IF WE'VE REACHED MAX STOCK
  const isMaxStock = item.quantity >= product.stock_quantity;
  const isOutOfStock = product.stock_quantity === 0;

  // Handle when user clicks on quantity to edit
  const handleQuantityClick = () => {
    setIsEditing(true);
    setInputValue(item.quantity.toString());
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (/^\d*$/.test(value)) {
      setInputValue(value);
    }
  };

  // Handle when user finishes editing (blur or Enter key)
  const handleInputBlur = () => {
    let newQty = parseInt(inputValue, 10);

    // Validate quantity
    if (isNaN(newQty) || newQty < 1) {
      newQty = 1;
    } else if (newQty > product.stock_quantity) {
      newQty = product.stock_quantity;
      alert(
        t("stockExceeded", "Only {{count}} items available in stock", {
          count: product.stock_quantity,
        }),
      );
    }

    setInputValue(newQty.toString());
    setIsEditing(false);

    // Update if different from current
    if (newQty !== item.quantity) {
      updateItemQuantity(item.cart_item_id, newQty);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleInputBlur();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setInputValue(item.quantity.toString());
    }
  };

  const productLink = product.product_id
    ? `/product/${product.product_id}`
    : null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between border-b last:border-b-0 py-6">
      {/* Product Info (Image + Name) - Takes half the space on desktop */}
      <div className="flex items-center space-x-4 w-full sm:w-1/2 mb-4 sm:mb-0">
        <Link to={productLink} state={{ product }} className="flex-shrink-0">
          <img
            src={imageUrl}
            alt={productName}
            className="w-20 h-20 object-cover rounded-lg shadow-sm"
          />
        </Link>
        <div className="flex flex-col">
          <Link
            to={productLink}
            state={{ product }}
            className="hover:underline flex-shrink-0"
          >
            <h3 className="font-semibold text-lg text-gray-900">
              {productName}
            </h3>
          </Link>
          <p className="text-gray-500 text-sm">
            {t("price", "Price")}: {validItemPrice.toFixed(2)} TL
          </p>
          {/* ✅ SHOW STOCK INFO */}
          <p className="text-xs text-gray-400 mt-1">
            {t("stock", "Stock")}: {product.stock_quantity}
          </p>
          {isMaxStock && (
            <p className="text-xs text-orange-500 font-semibold mt-1">
              {t("maxStockReached", "Max stock in cart")}
            </p>
          )}
        </div>
      </div>

      {/* Controls and Total - Takes half the space on desktop */}
      <div className="flex justify-between items-center w-full sm:w-1/2 mt-2 sm:mt-0">
        {/* Quantity Selector */}
        <div className="flex items-center border border-gray-300 rounded-lg shadow-sm">
          <button
            onClick={() =>
              updateItemQuantity(item.cart_item_id, item.quantity - 1)
            }
            className="p-3 text-[#916A80] hover:bg-gray-100 transition duration-150 rounded-l-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={item.quantity <= 1}
          >
            <FaMinus className="w-4 h-4" />
          </button>

          {/* ✅ EDITABLE QUANTITY INPUT */}
          {isEditing ? (
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              className="w-16 px-2 text-base font-medium text-center border-0 focus:outline-none focus:ring-2 focus:ring-[#916A80] rounded"
            />
          ) : (
            <span
              onClick={handleQuantityClick}
              className="px-4 text-base font-medium text-gray-700 cursor-pointer hover:bg-gray-50 py-2 rounded min-w-[3rem] text-center"
              title={t("clickToEdit", "Click to edit quantity")}
            >
              {item.quantity}
            </span>
          )}

          <button
            onClick={() =>
              updateItemQuantity(item.cart_item_id, item.quantity + 1)
            }
            className="p-3 text-[#916A80] hover:bg-gray-100 transition duration-150 rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isMaxStock || isOutOfStock}
            title={
              isMaxStock ? t("maxStockReached", "Max stock reached") : undefined
            }
          >
            <FaPlus className="w-4 h-4" />
          </button>
        </div>

        {/* Line Total */}
        <span className="font-bold text-lg text-gray-900 w-1/4 text-center">
          {lineTotal.toFixed(2)} TL
        </span>

        {/* Remove Button */}
        <button
          onClick={() => removeItemFromCart(item.cart_item_id)}
          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 transition duration-150 rounded-full"
          title={t("remove", "Remove")}
        >
          <FaTrashAlt className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
// -----------------------------------------------------

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shippingConfig, setShippingConfig] = useState(
    SHIPPING_CONFIG_DEFAULTS,
  );

  const { cartItems, updateItemQuantity, removeItemFromCart } = useCart();

  useEffect(() => {
    let isMounted = true;

    const loadShippingConfig = async () => {
      try {
        const config = await fetchShippingConfig();
        if (isMounted) {
          setShippingConfig(config);
        }
      } catch (error) {
        console.warn("Failed to load shipping config from backend:", error);
      }
    };

    loadShippingConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  // Calculate Subtotal and Total
  const subtotal = cartItems.reduce(
    (acc, item) =>
      acc + (item.Product ? parseFloat(item.Product.price) * item.quantity : 0),
    0,
  );

  const shippingCost =
    subtotal >= shippingConfig.freeShippingThreshold
      ? 0.0
      : shippingConfig.standardShippingRate;

  const total = subtotal + shippingCost;

  // Calculate remaining amount for the message
  const remainingForFreeShipping =
    shippingConfig.freeShippingThreshold - subtotal;
  const handleCheckout = () => {
    if (!ENABLE_CHECKOUT_UI) {
      return;
    }
    // Navigate to a separate /checkout page
    navigate("/checkout");
  };

  if (cartItems.length === 0) {
    return (
      <div className="canvas-background lg:px-[11rem] lg:py-[11rem] mx-auto  text-center bg-white rounded-xl shadow-lg  min-h-[70vh]">
        <FaShoppingCart className="w-20 h-20 text-[#AA7F96] mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t("cartEmptyTitle", "Your Shopping Cart is Empty")}
        </h1>
        <p className="text-gray-500 mb-6">
          {t(
            "cartEmptyMessage",
            "Looks like you haven't added anything to your cart yet. Let's find some cozy items!",
          )}
        </p>
        <button
          onClick={() => navigate("/")}
          className="bg-[#AA7F96] hover:bg-[#916A80] text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300 shadow-md"
        >
          {t("startShopping", "Start Shopping")}
        </button>
      </div>
    );
  }

  return (
    <div className="canvas-background mx-auto p-4 sm:p-6 lg:px-[11rem] lg:py-[8rem] min-h-screen">
      <h1 className="text-4xl font-extrabold text-[#83375B] mb-8 border-b-4 border-[#E0BBE4] pb-2">
        {t("shoppingCart", "My Shopping Cart")}
      </h1>

      {/* Responsive Grid Layout: Items on left (2/3), Summary on right (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Cart Items List */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-xl">
          {/* Column Headers for desktop view */}
          <div className="hidden sm:flex justify-between items-center text-sm font-semibold text-gray-600 border-b pb-3 mb-4">
            <span className="w-1/2">
              {t("productDetails", "Product Details")}
            </span>
            <div className="flex justify-between w-1/2 text-center">
              <span className="w-1/3 text-left">
                {t("quantity", "Quantity")}
              </span>
              <span className="w-1/3 text-center">{t("total", "Total")}</span>
              <span className="w-1/3 text-right">{t("remove", "Remove")}</span>
            </div>
          </div>

          {/* Item Rows */}
          {cartItems.map((item) => (
            <CartItemRow
              key={item.cart_item_id}
              item={item}
              updateItemQuantity={updateItemQuantity}
              removeItemFromCart={removeItemFromCart}
              t={t}
            />
          ))}
        </div>

        {/* Right Column: Order Summary */}
        <div className="lg:col-span-1 bg-gray-50 rounded-xl p-6 shadow-xl sticky top-20 h-fit border-t-4 border-[#AA7F96]">
          <h2 className="text-2xl font-bold text-[#83375B] mb-6">
            {t("orderSummary", "Order Summary")}
          </h2>

          {/* Subtotal */}
          <div className="flex justify-between text-lg font-medium text-gray-700 mb-3">
            <span>{t("subtotal", "Subtotal")}</span>
            <span>{subtotal.toFixed(2)} TL</span>
          </div>

          {/* Shipping Section */}
          <div className="py-4 border-y border-gray-200 mb-4">
            <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
              <FaTruck className="mr-2 w-5 h-5 text-[#AA7F96]" />{" "}
              {t("shipping", "Shipping")}
            </h3>
            <div className="flex justify-between text-base text-gray-600">
              <span>{t("estimatedCost", "Estimated Cost")}</span>
              <span className="font-semibold">
                {/* Dynamically display FREE or the shipping cost */}
                {shippingCost === 0.0 ? (
                  <span className="text-green-600">{t("free", "FREE")}</span>
                ) : (
                  `${shippingCost.toFixed(2)} TL`
                )}
              </span>
            </div>

            {/* Conditional Messaging */}
            {shippingCost === 0.0 ? (
              // Display success message when shipping is FREE
              <p className="text-xs text-green-600 mt-1">
                {t(
                  "freeShippingMessage",
                  "Congratulations! Your order qualifies for free shipping.",
                )}
              </p>
            ) : (
              // Display encouragement when shipping is NOT FREE
              <p className="text-xs text-gray-500 mt-1">
                {t(
                  "freeShippingEligibility",
                  "Add {{amount}} TL more for free shipping!",
                  {
                    amount: remainingForFreeShipping.toFixed(2),
                  },
                )}
              </p>
            )}
          </div>

          {/* Total */}
          <div className="flex justify-between text-2xl font-extrabold text-gray-900 mb-6">
            <span>{t("orderTotal", "Order Total")}</span>
            <span>{total.toFixed(2)} TL</span>
          </div>

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={!ENABLE_CHECKOUT_UI}
            className="w-full bg-[#83375B] hover:bg-[#6A2B47] text-white font-bold py-4 rounded-xl 
              transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-[#83375B] focus:ring-opacity-50 shadow-lg transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:transform-none"
          >
            {ENABLE_CHECKOUT_UI
              ? t("proceedToPayment", "Proceed to Payment")
              : t("checkoutUnavailable", "Checkout coming soon")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;
